<template>
  <div
    class="split-view-divider"
    :class="{ 'split-view-divider--dragging': isDragging }"
    data-testid="split-view-divider"
    role="separator"
    aria-orientation="vertical"
    aria-label="Drag to resize panels"
    :aria-valuenow="valueNow"
    :aria-valuemin="valueMin"
    :aria-valuemax="valueMax"
    title="Drag to resize. Double-click to reset."
    tabindex="0"
    @mousedown="onPointerDown"
    @touchstart="onPointerDown"
    @dblclick="$emit('reset')"
    @keydown="onKeyDown"
  >
    <div class="split-view-divider__track" />
    <div class="split-view-divider__handle">
      <span class="split-view-divider__dot" />
      <span class="split-view-divider__dot" />
      <span class="split-view-divider__dot" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  isDragging: boolean;
  leftRatio: number;
  minRatio: number;
  maxRatio: number;
}>();

// Report position as a whole-number percentage so screen readers announce
// "30 percent" after a keyboard nudge instead of "0.3 of 1". aria-valuemin
// and aria-valuemax reflect the clamp bounds, not 0–100.
const valueNow = computed(() => Math.round(props.leftRatio * 100));
const valueMin = computed(() => Math.round(props.minRatio * 100));
const valueMax = computed(() => Math.round(props.maxRatio * 100));

const emit = defineEmits<{
  pointerDown: [event: MouseEvent | TouchEvent];
  reset: [];
  nudge: [direction: 'left' | 'right'];
}>();

const onPointerDown = (event: MouseEvent | TouchEvent) => {
  emit('pointerDown', event);
};

const onKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    emit('nudge', 'left');
  } else if (event.key === 'ArrowRight') {
    event.preventDefault();
    emit('nudge', 'right');
  }
};
</script>

<style scoped>
.split-view-divider {
  @apply relative flex items-center justify-center cursor-col-resize select-none;
  /* Defined on SplitViewContainer and inherited via CSS custom-property
     cascade. The fallback keeps the divider a sensible width if it's ever
     mounted outside a container (e.g. in a unit test). */
  width: var(--split-divider-width, 12px);
  flex-shrink: 0;
  touch-action: none;
}

.split-view-divider__track {
  @apply absolute top-0 bottom-0 w-px bg-zinc-200 dark:bg-zinc-700 transition-colors;
  left: 50%;
  transform: translateX(-50%);
}

.split-view-divider__handle {
  @apply relative flex flex-col items-center justify-center gap-0.5 px-1 py-1.5 rounded-md bg-zinc-100 dark:bg-zinc-700 border border-zinc-200 dark:border-zinc-600 transition-all;
}

.split-view-divider__dot {
  @apply block w-0.5 h-0.5 rounded-full bg-zinc-500 dark:bg-zinc-400 transition-colors;
}

.split-view-divider:hover .split-view-divider__track,
.split-view-divider:focus-visible .split-view-divider__track,
.split-view-divider--dragging .split-view-divider__track {
  @apply bg-blue-400 dark:bg-blue-500;
  width: 2px;
}

.split-view-divider:hover .split-view-divider__handle,
.split-view-divider:focus-visible .split-view-divider__handle,
.split-view-divider--dragging .split-view-divider__handle {
  @apply bg-blue-50 dark:bg-blue-900/40 border-blue-400 dark:border-blue-500;
}

.split-view-divider:hover .split-view-divider__dot,
.split-view-divider:focus-visible .split-view-divider__dot,
.split-view-divider--dragging .split-view-divider__dot {
  @apply bg-blue-600 dark:bg-blue-400;
}

.split-view-divider:focus-visible {
  @apply outline-none;
}

.split-view-divider:focus-visible::after {
  content: '';
  @apply absolute inset-y-0 -inset-x-1 rounded ring-2 ring-blue-500/50 pointer-events-none;
}
</style>
